import { useEffect, useMemo, useState, type ReactNode } from 'react';
import api, {
  DriftStatus,
  FederationStatus,
  ProbeStatus,
  ProbeSource,
  ServiceVersionApp,
  ServiceVersionsMarketplaceResponse,
  ServiceVersionsPlatformResponse,
  PlatformServiceRow,
  extractApiError,
} from '../services/api';
import { labelForFederationStatus } from '../services/status-labels';

/**
 * Service-Versionen — operator version-drift console.
 *
 * Two tabs:
 *   - Marketplace-Apps: catalog-latest SemVer vs. the version really
 *     installed on each Enterprise tenant.
 *   - Plattform-Dienste: declared/expected version vs. the LIVE
 *     running version on the central control-plane and per tenant.
 *
 * Both views are cross-tenant (federation fan-out), read-only, and
 * fail-soft — an unreachable tenant or a down service degrades to an
 * "unbekannt" cell rather than blanking the table.
 */

type Tab = 'marketplace' | 'platform';

// ---- shared drift badge -------------------------------------------------

const DRIFT_LABEL: Record<DriftStatus, string> = {
  current: 'aktuell',
  outdated: 'veraltet',
  ahead: 'neuer als Katalog',
  unknown: 'unbekannt',
  notInCatalog: 'nicht im Katalog',
};

const DRIFT_COLORS: Record<DriftStatus, string> = {
  current: 'bg-green-100 text-green-800',
  outdated: 'bg-amber-100 text-amber-800',
  ahead: 'bg-blue-100 text-blue-800',
  unknown: 'bg-slate-100 text-slate-500',
  notInCatalog: 'bg-red-100 text-red-700',
};

const FEDERATION_COLORS: Record<FederationStatus, string> = {
  reachable: 'bg-green-100 text-green-800',
  unreachable: 'bg-amber-100 text-amber-800',
  auth_failed: 'bg-red-100 text-red-700',
  timeout: 'bg-amber-100 text-amber-800',
  disabled: 'bg-slate-200 text-slate-600',
  unknown: 'bg-slate-100 text-slate-500',
  local: 'bg-sky-100 text-sky-700',
};

function DriftBadge({ drift, version }: { drift: DriftStatus; version?: string | null }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] ${DRIFT_COLORS[drift]}`}
      title={DRIFT_LABEL[drift]}
    >
      {version ? <span className="font-mono">{version}</span> : null}
      <span>{DRIFT_LABEL[drift]}</span>
    </span>
  );
}

function FederationBadge({ status }: { status: FederationStatus }) {
  return (
    <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] ${FEDERATION_COLORS[status]}`}>
      {labelForFederationStatus(status)}
    </span>
  );
}

function StatTile({
  label, value, sub, tone,
}: { label: string; value: number; sub?: string; tone?: 'success' | 'warning' | 'danger' }) {
  const toneClass =
    tone === 'success' ? 'text-green-700' :
    tone === 'warning' ? 'text-amber-700' :
    tone === 'danger' ? 'text-red-700' :
    'text-slate-800';
  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

// =========================================================================
// Page shell
// =========================================================================

export function ServiceVersionsPage() {
  const [tab, setTab] = useState<Tab>('marketplace');

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold">Service-Versionen</h2>
        <p className="text-sm text-slate-500">
          Welche Versionen haben wir im Katalog/deklariert — und welche laufen real über alle Tenants?
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        <TabButton active={tab === 'marketplace'} onClick={() => setTab('marketplace')}>
          Marketplace-Apps
        </TabButton>
        <TabButton active={tab === 'platform'} onClick={() => setTab('platform')}>
          Plattform-Dienste
        </TabButton>
      </div>

      {tab === 'marketplace' ? <MarketplaceVersionsTab /> : <PlatformVersionsTab />}
    </div>
  );
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
        active
          ? 'border-neutral-800 text-neutral-900'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

function Loading({ what }: { what: string }) {
  return <div className="text-slate-500">Lade {what}…</div>;
}

function ErrorBox({ error }: { error: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
      Fehler beim Laden: {error}
    </div>
  );
}

// =========================================================================
// Tab 1 — Marketplace-App-Versionen
// =========================================================================

type MarketplaceDriftFilter = 'all' | DriftStatus;

function MarketplaceVersionsTab() {
  const [data, setData] = useState<ServiceVersionsMarketplaceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [driftFilter, setDriftFilter] = useState<MarketplaceDriftFilter>('all');
  const [drillApp, setDrillApp] = useState<ServiceVersionApp | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<ServiceVersionsMarketplaceResponse>('/service-versions/marketplace')
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo<string[]>(() => {
    if (!data) return [];
    return Array.from(new Set(data.apps.map((a) => a.category))).sort();
  }, [data]);

  const filteredApps = useMemo<ServiceVersionApp[]>(() => {
    if (!data) return [];
    return data.apps.filter((app) => {
      if (categoryFilter !== 'all' && app.category !== categoryFilter) return false;
      if (driftFilter !== 'all') {
        if ((app.driftCounts[driftFilter] ?? 0) === 0) return false;
      }
      return true;
    });
  }, [data, categoryFilter, driftFilter]);

  const stats = useMemo(() => {
    if (!data) return { totalApps: 0, tenants: 0, outdated: 0, notInCatalog: 0 };
    let outdated = 0;
    let notInCatalog = 0;
    for (const app of data.apps) {
      if ((app.driftCounts.outdated ?? 0) > 0) outdated += 1;
      if (!app.inCatalog) notInCatalog += 1;
    }
    return { totalApps: data.apps.length, tenants: data.tenants.length, outdated, notInCatalog };
  }, [data]);

  if (loading) return <Loading what="Marketplace-Versionen" />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return <div className="text-slate-500">Keine Daten verfügbar.</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Apps gesamt" value={stats.totalApps} />
        <StatTile label="Tenants" value={stats.tenants} />
        <StatTile label="Apps mit veralteten Installationen" value={stats.outdated} tone="warning" />
        <StatTile label="Nicht im Katalog" value={stats.notInCatalog} tone="danger" />
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <label className="text-sm">
          <span className="block text-slate-700 mb-1">Kategorie</span>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-2 py-1 border rounded bg-white"
          >
            <option value="all">Alle</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-slate-700 mb-1">Drift</span>
          <select
            value={driftFilter}
            onChange={(e) => setDriftFilter(e.target.value as MarketplaceDriftFilter)}
            className="px-2 py-1 border rounded bg-white"
          >
            <option value="all">Alle</option>
            <option value="outdated">Mit veralteten</option>
            <option value="ahead">Mit neueren</option>
            <option value="current">Mit aktuellen</option>
            <option value="unknown">Mit unbekannten</option>
            <option value="notInCatalog">Nicht im Katalog</option>
          </select>
        </label>
        <div className="text-xs text-slate-500 pb-1">
          {filteredApps.length} von {data.apps.length} Apps angezeigt.
        </div>
      </div>

      {data.tenants.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm">
          Keine aktiven Enterprise-Federation-Targets registriert. Installierte Versionen erscheinen,
          sobald mindestens ein <code>EnterpriseTarget</code> aktiviert ist.
        </div>
      ) : (
        <MarketplaceTable
          apps={filteredApps}
          tenants={data.tenants}
          federation={data.federation}
          onRowClick={setDrillApp}
        />
      )}

      {drillApp ? (
        <MarketplaceDrillModal
          app={drillApp}
          federation={data.federation}
          onClose={() => setDrillApp(null)}
        />
      ) : null}
    </div>
  );
}

interface MarketplaceTableProps {
  apps: ServiceVersionApp[];
  tenants: string[];
  federation: ServiceVersionsMarketplaceResponse['federation'];
  onRowClick: (app: ServiceVersionApp) => void;
}

function MarketplaceTable({ apps, tenants, federation, onRowClick }: MarketplaceTableProps) {
  return (
    <div className="overflow-x-auto bg-white rounded shadow-sm border border-slate-200">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2">App</th>
            <th className="text-left px-3 py-2">Katalog (latest)</th>
            {tenants.map((t) => (
              <th key={t} className="text-center px-2 py-2" title={federation[t]?.status ?? 'unknown'}>
                <div>{t}</div>
                <FederationBadge status={(federation[t]?.status ?? 'unknown') as FederationStatus} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {apps.length === 0 ? (
            <tr>
              <td colSpan={2 + tenants.length} className="text-center text-slate-500 py-6">
                Keine Apps passen zum Filter.
              </td>
            </tr>
          ) : apps.map((app) => {
            const byTenant = new Map(app.installations.map((i) => [i.tenantSlug, i]));
            return (
              <tr
                key={app.slug}
                onClick={() => onRowClick(app)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{app.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{app.slug} · {app.category}</div>
                </td>
                <td className="px-3 py-2">
                  {app.inCatalog ? (
                    <span className="font-mono text-slate-700">{app.catalogLatestSemver ?? '—'}</span>
                  ) : (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                      nicht im Katalog
                    </span>
                  )}
                </td>
                {tenants.map((t) => {
                  const inst = byTenant.get(t);
                  const fed = (federation[t]?.status ?? 'unknown') as FederationStatus;
                  return (
                    <td key={t} className="px-2 py-2 text-center">
                      {inst ? (
                        <DriftBadge drift={inst.drift} version={inst.installedSemver} />
                      ) : fed === 'reachable' ? (
                        <span aria-label="nicht installiert" className="text-slate-400">—</span>
                      ) : (
                        <span aria-label="unbekannt (Tenant nicht erreichbar)" className="text-slate-400">?</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MarketplaceDrillModal({
  app, federation, onClose,
}: {
  app: ServiceVersionApp;
  federation: ServiceVersionsMarketplaceResponse['federation'];
  onClose: () => void;
}) {
  return (
    <ModalShell title={app.name} subtitle={`${app.slug} · ${app.category}`} onClose={onClose}>
      <div className="text-sm mb-3">
        Katalog-Version: <span className="font-mono">{app.catalogLatestSemver ?? '—'}</span>
        {!app.inCatalog ? <span className="ml-2 text-red-700">(nicht im Katalog)</span> : null}
      </div>
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left py-1">Tenant</th>
            <th className="text-left py-1">Installiert</th>
            <th className="text-left py-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {app.installations.length === 0 ? (
            <tr><td colSpan={3} className="text-slate-400 py-2">Auf keinem erreichbaren Tenant installiert.</td></tr>
          ) : app.installations.map((i) => (
            <tr key={i.tenantSlug} className="border-t border-slate-100">
              <td className="py-1 font-mono text-slate-700">{i.tenantSlug}</td>
              <td className="py-1 font-mono">{i.installedSemver ?? '—'}</td>
              <td className="py-1">
                <DriftBadge drift={i.drift} />
                <span className="ml-2 text-[10px] text-slate-400">
                  {labelForFederationStatus((federation[i.tenantSlug]?.status ?? 'unknown') as FederationStatus)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ModalShell>
  );
}

// =========================================================================
// Tab 2 — Plattform-Dienst-Versionen
// =========================================================================

function PlatformVersionsTab() {
  const [data, setData] = useState<ServiceVersionsPlatformResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drillSvc, setDrillSvc] = useState<PlatformServiceRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<ServiceVersionsPlatformResponse>('/service-versions/platform')
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => {
    if (!data) return { services: 0, tenants: 0, centralDrift: 0, tenantDrift: 0 };
    let centralDrift = 0;
    let tenantDrift = 0;
    for (const s of data.services) {
      if (s.central.drift === 'outdated' || s.central.drift === 'ahead') centralDrift += 1;
      if ((s.driftCounts.outdated ?? 0) + (s.driftCounts.ahead ?? 0) > 0) tenantDrift += 1;
    }
    return { services: data.services.length, tenants: data.tenants.length, centralDrift, tenantDrift };
  }, [data]);

  if (loading) return <Loading what="Plattform-Dienst-Versionen" />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return <div className="text-slate-500">Keine Daten verfügbar.</div>;

  if (data.services.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm">
        Keine Dienste registriert. Pflege die Liste der zu prüfenden Dienste über die
        Umgebungsvariable <code>SERVICE_VERSION_REGISTRY</code> (JSON-Array) in der
        platform-admin ConfigMap.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Dienste" value={stats.services} />
        <StatTile label="Tenants" value={stats.tenants} />
        <StatTile label="Drift zentral" value={stats.centralDrift} tone="warning" />
        <StatTile label="Dienste mit Tenant-Drift" value={stats.tenantDrift} tone="warning" />
      </div>

      <PlatformTable
        services={data.services}
        tenants={data.tenants}
        federation={data.federation}
        onRowClick={setDrillSvc}
      />

      {drillSvc ? (
        <PlatformDrillModal svc={drillSvc} federation={data.federation} onClose={() => setDrillSvc(null)} />
      ) : null}
    </div>
  );
}

interface PlatformTableProps {
  services: PlatformServiceRow[];
  tenants: string[];
  federation: ServiceVersionsPlatformResponse['federation'];
  onRowClick: (svc: PlatformServiceRow) => void;
}

function PlatformTable({ services, tenants, federation, onRowClick }: PlatformTableProps) {
  return (
    <div className="overflow-x-auto bg-white rounded shadow-sm border border-slate-200">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2">Dienst</th>
            <th className="text-left px-3 py-2">Erwartet</th>
            <th className="text-left px-3 py-2">Zentral (live)</th>
            {tenants.map((t) => (
              <th key={t} className="text-center px-2 py-2" title={federation[t]?.status ?? 'unknown'}>
                <div>{t}</div>
                <FederationBadge status={(federation[t]?.status ?? 'unknown') as FederationStatus} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {services.map((svc) => {
            const byTenant = new Map(svc.tenants.map((c) => [c.tenantSlug, c]));
            return (
              <tr
                key={svc.id}
                onClick={() => onRowClick(svc)}
                className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${svc.thirdParty ? 'opacity-60' : ''}`}
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{svc.name}</div>
                  <div className="text-xs text-slate-500 font-mono">
                    {svc.id}{svc.thirdParty ? ' · 3rd-party' : ''}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-slate-700">{svc.expectedVersion ?? '—'}</td>
                <td className="px-3 py-2">
                  {svc.thirdParty ? (
                    <span className="text-[11px] text-slate-400" title="nur Image-Tag, nicht geprüft">
                      nicht geprüft
                    </span>
                  ) : svc.central.status === 'ok' ? (
                    <>
                      <DriftBadge drift={svc.central.drift} version={svc.central.liveVersion} />
                      <SourceTag source={svc.central.source} />
                    </>
                  ) : (
                    <ProbeStatusBadge status={svc.central.status} />
                  )}
                </td>
                {tenants.map((t) => {
                  const cell = byTenant.get(t);
                  const fed = (federation[t]?.status ?? 'unknown') as FederationStatus;
                  return (
                    <td key={t} className="px-2 py-2 text-center">
                      {svc.thirdParty ? (
                        <span className="text-slate-300">—</span>
                      ) : cell ? (
                        cell.status === 'ok'
                          ? <><DriftBadge drift={cell.drift} version={cell.liveVersion} /><SourceTag source={cell.source} /></>
                          : <ProbeStatusBadge status={cell.status} />
                      ) : fed === 'reachable' ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <span aria-label="unbekannt (Tenant nicht erreichbar)" className="text-slate-400">?</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SourceTag({ source }: { source: ProbeSource }) {
  // Tells the operator HOW the live version was obtained, so an
  // authoritative /api/version read isn't confused with an inferred
  // image tag. Hidden for unprobed ('none') rows.
  if (source === 'none') return null;
  const m =
    source === 'image'
      ? { label: 'Image', title: 'Version aus dem laufenden Container-Image-Tag (Prometheus)' }
      : { label: 'API', title: 'Version vom /api/version-Endpunkt des Dienstes' };
  return (
    <span
      className="ml-1 inline-block px-1 py-0.5 rounded text-[9px] uppercase tracking-wide bg-slate-100 text-slate-500 align-middle"
      title={m.title}
    >
      {m.label}
    </span>
  );
}

function ProbeStatusBadge({ status }: { status: ProbeStatus }) {
  const map: Record<ProbeStatus, { label: string; cls: string }> = {
    ok: { label: 'ok', cls: 'bg-green-100 text-green-800' },
    unreachable: { label: 'nicht erreichbar', cls: 'bg-amber-100 text-amber-800' },
    'no-version-endpoint': { label: 'kein /version', cls: 'bg-slate-200 text-slate-600' },
    'third-party': { label: 'nicht geprüft', cls: 'bg-slate-100 text-slate-500' },
  };
  const m = map[status];
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] ${m.cls}`}>{m.label}</span>;
}

function PlatformDrillModal({
  svc, federation, onClose,
}: {
  svc: PlatformServiceRow;
  federation: ServiceVersionsPlatformResponse['federation'];
  onClose: () => void;
}) {
  return (
    <ModalShell title={svc.name} subtitle={`${svc.id}${svc.thirdParty ? ' · 3rd-party' : ''}`} onClose={onClose}>
      <div className="text-sm mb-3 space-y-1">
        <div>Erwartet: <span className="font-mono">{svc.expectedVersion ?? '—'}</span></div>
        <div>
          Zentral: <span className="font-mono">{svc.central.liveVersion ?? '—'}</span>
          <SourceTag source={svc.central.source} />
          {svc.central.commit ? <span className="ml-2 text-xs text-slate-400 font-mono">{svc.central.commit}</span> : null}
          <span className="ml-2"><DriftBadge drift={svc.central.drift} /></span>
        </div>
      </div>
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left py-1">Tenant</th>
            <th className="text-left py-1">Live</th>
            <th className="text-left py-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {svc.tenants.length === 0 ? (
            <tr><td colSpan={3} className="text-slate-400 py-2">Keine erreichbaren Tenant-Daten.</td></tr>
          ) : svc.tenants.map((c) => (
            <tr key={c.tenantSlug} className="border-t border-slate-100">
              <td className="py-1 font-mono text-slate-700">{c.tenantSlug}</td>
              <td className="py-1 font-mono">{c.liveVersion ?? '—'}</td>
              <td className="py-1">
                {c.status === 'ok' ? <DriftBadge drift={c.drift} /> : <ProbeStatusBadge status={c.status} />}
                <span className="ml-2 text-[10px] text-slate-400">
                  {labelForFederationStatus((federation[c.tenantSlug]?.status ?? 'unknown') as FederationStatus)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ModalShell>
  );
}

// ---- shared modal shell -------------------------------------------------

function ModalShell({
  title, subtitle, onClose, children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Detail-Ansicht für ${title}`}
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded shadow-xl w-full max-w-xl p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            {subtitle ? <div className="text-xs text-slate-500 font-mono">{subtitle}</div> : null}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Schliessen">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// =========================================================================
// Nav badge — count of marketplace apps with any outdated installation.
// Polls the (lighter) marketplace endpoint every 30s; silent on failure.
// =========================================================================

const BADGE_POLL_INTERVAL_MS = 30_000;

export function ServiceVersionsNavBadge() {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    function load(): void {
      api
        .get<ServiceVersionsMarketplaceResponse>('/service-versions/marketplace')
        .then((r) => {
          if (cancelled) return;
          let outdated = 0;
          for (const app of r.data.apps) {
            if ((app.driftCounts.outdated ?? 0) > 0) outdated += 1;
          }
          setCount(outdated);
        })
        .catch(() => { if (!cancelled) setCount(null); });
    }
    load();
    const id = window.setInterval(load, BADGE_POLL_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  if (count === null || count === 0) return null;
  return (
    <span
      className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
      aria-label={`${count} Apps mit veralteten Installationen`}
      title={`${count} Apps mit veralteten Installationen`}
    >
      {count}
    </span>
  );
}
